import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PiCurrencyCircleDollarDuotone,
  PiTrashDuotone,
  PiTrophyDuotone,
  PiCheckCircleDuotone,
  PiClockCountdownDuotone,
  PiPencilSimpleDuotone,
  PiTargetDuotone,
  PiArrowClockwiseDuotone,
  PiArrowSquareOutDuotone,
  PiCalendarDuotone,
  PiClockDuotone,
} from 'react-icons/pi';
import {
  chooseSideBetOption,
  createSideBet,
  deleteChallenge,
  deleteSideBet,
  deleteSideBetChoice,
  getChallenge,
  getLeaderboard,
  getSideBets,
  recalculateMainScores,
  resolveSideBet,
  updateSideBet,
} from '../api/challengeService';
import { getBets, createBet, deleteBet } from '../api/betService';
import { getEvent, updateEventResult } from '../api/eventService';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Badge from '../shared/common/Badge';
import Breadcrumb from '../shared/common/Breadcrumb';
import Input from '../shared/common/Input';
import EmptyState from '../shared/common/EmptyState';
import Modal, { ConfirmModal } from '../shared/common/Modal';
import { SkeletonList } from '../shared/common/Skeleton';
import { formatPoints, getVisibleScoringRules } from '../shared/business/scoring/ScoringRulesSummary';
import { useAuth } from '../app/providers/AuthContext';
import { ACCESS_DENIED_MESSAGE, accessAwareMessage, isAccessDeniedError } from '../api/api';
import { getRoom } from '../api/roomService';
import {
  allowedOutcomeCodes,
  formatMainOutcome,
  isScoreBasedChallenge,
  outcomeCodeShortLabel,
  outcomeCodesForScore,
  validateMainOutcome,
  DrawPolicy,
} from '../shared/business/scoring/mainOutcomeHelpers';
import { formatBreakdownText, formatMatchedRuleType } from '../shared/business/scoring/scoringBreakdownHelpers';
import { OutcomeScheme } from '../models/eventType';
import { getEventType } from '../api/eventTypeService';
import {
  isScoreBasedScheme,
  isCustomScheme,
  outcomeSchemeLabel,
  outcomeSchemeIcon,
} from '../shared/business/eventType/outcomeSchemeHelpers';
import { generateOutcomes as generateOutcomesFromEventType } from '../shared/business/eventType/eventTypeCapabilities';

const STATUS_MAP = { open: 'open', closed: 'closed', resolved: 'resolved' };

const EVENT_STATUS_LABELS = {
  scheduled: 'запланировано',
  inProgress: 'идёт',
  resultFixed: 'результат зафиксирован',
  archived: 'архивировано',
};

const EVENT_STATUS_BADGE = {
  scheduled: 'open',
  inProgress: 'closed',
  resultFixed: 'resolved',
  archived: 'resolved',
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const resolveEventStatus = (event) => {
  if (!event) return null;
  if (event.archivedAt) return 'archived';
  if (event.result) return 'resultFixed';
  const startDate = event.startDate || event.eventDate || event.date;
  if (startDate && new Date(startDate).getTime() <= Date.now()) return 'inProgress';
  if (startDate) return 'scheduled';
  return 'inProgress';
};

const formatDateRange = (event) => {
  if (!event) return null;
  const start = formatDate(event.startDate || event.eventDate || event.date);
  const end = formatDate(event.endDate);
  if (start && end) return `${start} — ${end}`;
  return start || end;
};

function findLeaderboardEntry(leaderboard, userId) {
  return leaderboard.find((entry) => String(entry.userId) === String(userId));
}

function getResolvedBetScore(bet, leaderboardEntry) {
  return Number(leaderboardEntry?.score ?? bet?.score ?? 0);
}

function formatBetScoreBreakdown(bet, leaderboardEntry) {
  const sideBetScore = Number(leaderboardEntry?.sideBetScore ?? 0);
  if (sideBetScore <= 0) return null;

  const mainScore = Number(leaderboardEntry?.mainScore ?? bet?.score ?? 0);
  return `Основной: ${mainScore} · Особые: ${sideBetScore}`;
}

function totalUserScore(mainBreakdown, sideBetScore) {
  return Number(mainBreakdown?.totalScore || 0) + Number(sideBetScore || 0);
}

const DEFAULT_SIDE_BET_OPTIONS = ['Да', 'Нет'];
const BET_MODE_MONEY = 'MONEY';
const BET_MODE_POINTS = 'POINTS';
const DEFAULT_MAX_MULTIPLIER = 3;
const DEFAULT_BET_MULTIPLIER = 1;
const BET_HIDDEN_VALUE_MESSAGE = 'Значение ставки скрыто до начала события';
const SIDE_BET_DETAILS_EMPTY_MESSAGE = 'Пока никто не выбрал этот вариант.';
const MONEY_VALUE_ERROR = 'Укажи корректную сумму ставки';
const RECALCULATE_SUCCESS_MESSAGE = 'Очки пересчитаны';
const RECALCULATE_ERROR_MESSAGE = 'Не удалось пересчитать очки';
const RECALCULATE_BUTTON_TITLE = 'Пересчитать очки и событие';

const buildRoomPagePath = (roomId) => `/rooms/${roomId}`;

const buildChallengeListPath = (roomId, leagueId) => (
  leagueId ? `/rooms/${roomId}/leagues/${leagueId}/challenges` : buildRoomPagePath(roomId)
);

const buildChallengeEditPath = (roomId, leagueId, challengeId) => (
  `${buildChallengeListPath(roomId, leagueId)}/${challengeId}/edit`
);

export default function ChallengeViewPage() {
  const { roomId, leagueId, challengeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [bets, setBets] = useState([]);
  const [sideBets, setSideBets] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Форма ставки
  const [outcome, setOutcome] = useState('');
  const [value, setValue] = useState('');
  const [multiplier, setMultiplier] = useState(String(DEFAULT_BET_MULTIPLIER));
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [outcomeCode, setOutcomeCode] = useState('');
  const [betErrors, setBetErrors] = useState({});
  const [betLoading, setBetLoading] = useState(false);

  // Особые исходы
  const [sideBetTitle, setSideBetTitle] = useState('');
  const [sideBetDescription, setSideBetDescription] = useState('');
  const [sideBetReward, setSideBetReward] = useState('1');
  const [sideBetOptions, setSideBetOptions] = useState(DEFAULT_SIDE_BET_OPTIONS.join('\n'));
  const [sideBetErrors, setSideBetErrors] = useState({});
  const [sideBetLoading, setSideBetLoading] = useState(false);
  const [sideBetActionId, setSideBetActionId] = useState(null);
  const [sideBetFormOpen, setSideBetFormOpen] = useState(false);
  const [sideBetSectionEnabled, setSideBetSectionEnabled] = useState(false);
  const [sideBetEditingId, setSideBetEditingId] = useState(null);
  const [sideBetChoiceEditingIds, setSideBetChoiceEditingIds] = useState([]);
  const [sideBetDeleteTarget, setSideBetDeleteTarget] = useState(null);
  const [sideBetDeleting, setSideBetDeleting] = useState(false);
  const [sideBetDetailsTarget, setSideBetDetailsTarget] = useState(null);

  // Удаление ставки
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

// Удаление пари
  const [challengeDeleteConfirmOpen, setChallengeDeleteConfirmOpen] = useState(false);
  const [cascadeDeleteConfirmOpen, setCascadeDeleteConfirmOpen] = useState(false);

  // Связанное событие
  const [event, setEvent] = useState(null);
  const [eventResult, setEventResult] = useState('');
  const [eventMainResult, setEventMainResult] = useState({ homeScore: '', awayScore: '', outcomeCode: '' });
  const [eventResultError, setEventResultError] = useState('');
  const [eventResultSuccess, setEventResultSuccess] = useState('');
  const [eventResultLoading, setEventResultLoading] = useState(false);
  const [eventType, setEventType] = useState(null);
  const [recalculatingMainScores, setRecalculatingMainScores] = useState(false);
  const [recalculateMessage, setRecalculateMessage] = useState('');

  const isLeagueScope = Boolean(leagueId);
  const challengeListPath = buildChallengeListPath(roomId, leagueId);
  const roomLabel = room?.name || 'Комната';
  const isResolved = challenge?.status === 'RESOLVED' || challenge?.status === 'resolved';
  const isClosed = challenge?.status === 'CLOSED' || challenge?.status === 'closed';
  const canBet = !error && challenge?.bettingAvailable === true;
  const roomBetMode = String(room?.betMode || BET_MODE_POINTS).toUpperCase();
  const isMoneyMode = roomBetMode === BET_MODE_MONEY;
  const isConfidenceMultiplierEnabled = Boolean(room?.confidenceMultiplierEnabled);
  const maxMultiplier = positiveInteger(room?.maxMultiplier, DEFAULT_MAX_MULTIPLIER);
  const selectedMultiplier = positiveInteger(multiplier, DEFAULT_BET_MULTIPLIER);
  const multiplierOptions = Array.from({ length: maxMultiplier }, (_, index) => index + 1);
  const currentUserId = user?.id ?? user?.userId;
  const hasOwnBet = bets.some(isOwnBet);
  const canCreateMainBet = canBet && !hasOwnBet;
  const canManageChallenge = challenge?.editable === true && challenge?.managementAvailable === true;
  const canRecalculateMainScores = isResolved && challenge?.managementAvailable === true;
  const canManageSideBets = challenge?.sideBetManageAvailable === true;
  const eventStartTime = challenge?.eventStartDate ? new Date(challenge.eventStartDate).getTime() : null;
  const eventStarted = Number.isFinite(eventStartTime) && eventStartTime <= Date.now();
  const canResolveSideBets = !error && canManageSideBets && eventStarted;
  const editingSideBet = sideBets.find((sideBet) => String(sideBet.id) === String(sideBetEditingId)) || null;
  const sideBetOptionsLocked = Boolean(editingSideBet && Number(editingSideBet.choicesCount || 0) > 0);
  const shouldShowSideBetDetails = sideBetSectionEnabled || sideBets.length > 0;
  const canToggleSideBetDetails = canBet && canManageSideBets && sideBets.length === 0;
  const shouldRenderSideBetSection = shouldShowSideBetDetails || canToggleSideBetDetails;
  const shouldShowSideBetEmptyState = shouldShowSideBetDetails && sideBets.length === 0 && !sideBetFormOpen;
  const sideBetScore = sideBets.reduce((sum, sideBet) => sum + Number(sideBet.currentUserScore || 0), 0);
  const outcomeScheme = eventType?.outcomeScheme || null;
  const isSchemeScoreBased = outcomeScheme === OutcomeScheme.SCORE_BASED;
  const isSchemeWinnerOnly = outcomeScheme === OutcomeScheme.WINNER_ONLY;
  const isSchemeWinnerWithMethod = outcomeScheme === OutcomeScheme.WINNER_WITH_METHOD;
  const isSchemePlacement = outcomeScheme === OutcomeScheme.PLACEMENT;
  const isSchemeIndividualScore = outcomeScheme === OutcomeScheme.INDIVIDUAL_SCORE;
  const isSchemeCustom = outcomeScheme === OutcomeScheme.CUSTOM;
  // Для WINNER_WITH_METHOD — выбранный победитель и способ
  const [winnerChoice, setWinnerChoice] = useState('');
  const [winMethodChoice, setWinMethodChoice] = useState('');
  // Для PLACEMENT — выбранный участник на место
  const [placementChoice, setPlacementChoice] = useState('');
  // Для INDIVIDUAL_SCORE — место + балл
  const [individualPlacement, setIndividualPlacement] = useState('');
  const [individualScore, setIndividualScore] = useState('');

  const loadBets = () =>
    getBets(roomId, leagueId, challengeId)
      .then((data) => setBets(Array.isArray(data) ? data : []))
      .catch((err) => {
        if (isAccessDeniedError(err)) {
          setError(ACCESS_DENIED_MESSAGE);
        }
      });

  const loadSideBets = () =>
    getSideBets(roomId, leagueId, challengeId)
      .then((data) => setSideBets(Array.isArray(data) ? data : []))
      .catch((err) => {
        if (isAccessDeniedError(err)) {
          setError(ACCESS_DENIED_MESSAGE);
        }
      });

  useEffect(() => {
document.title = 'BetMate — Пари';
    Promise.all([
      getRoom(roomId),
      getChallenge(roomId, leagueId, challengeId),
      getBets(roomId, leagueId, challengeId),
      getSideBets(roomId, leagueId, challengeId),
    ])
      .then(([roomData, challengeData, betsData, sideBetsData]) => {
        setRoom(roomData);
        setChallenge(challengeData);
        setBets(Array.isArray(betsData) ? betsData : []);
        setSideBets(Array.isArray(sideBetsData) ? sideBetsData : []);
// Загружаем лидерборд для завершённых пари
        const resolved = challengeData?.status === 'RESOLVED' || challengeData?.status === 'resolved';
        if (resolved) {
          getLeaderboard(roomId, leagueId, challengeId)
            .then((lb) => setLeaderboard(Array.isArray(lb) ? lb : []))
            .catch(() => {});
        }
        // Загружаем связанное событие
        if (challengeData?.eventId) {
          getEvent(challengeData.eventId)
            .then((eventData) => {
              setEvent(eventData);
              setEventResult(eventData.result || '');
              if (eventData.eventTypeId) {
                getEventType(eventData.eventTypeId)
                  .then((et) => setEventType(et))
                  .catch(() => {});
              }
            })
            .catch(() => {});
        }
      })
      .catch((err) => setError(accessAwareMessage(err, 'Не удалось загрузить данные')))
      .finally(() => setLoading(false));
  }, [roomId, leagueId, challengeId]);

  const handleCreateBet = async (e) => {
    e.preventDefault();
    if (!canCreateMainBet) {
setBetErrors({ global: hasOwnBet ? 'Вы уже сделали ставку в этом пари' : 'Ставки по этому пари уже недоступны' });
      return;
    }
    const errs = {};
    const isStructured = isScoreBasedChallenge(challenge) || isSchemeScoreBased;

    if (isStructured) {
      const validation = validateMainOutcome(homeScore, awayScore, outcomeCode, challenge.drawPolicy);
      if (!validation.valid) {
        Object.assign(errs, validation.errors);
      }
    } else if (isSchemeWinnerOnly) {
      if (!winnerChoice.trim()) errs.outcome = 'Выбери победителя';
    } else if (isSchemeWinnerWithMethod) {
      if (!winnerChoice.trim()) errs.outcome = 'Выбери победителя';
      if (!winMethodChoice.trim()) errs.outcome = errs.outcome || 'Выбери способ победы';
    } else if (isSchemePlacement) {
      if (!placementChoice.trim()) errs.outcome = 'Выбери место';
    } else if (isSchemeIndividualScore) {
      if (!individualPlacement.trim()) errs.outcome = 'Выбери место';
      if (!individualScore.trim()) errs.outcome = errs.outcome || 'Укажи балл';
    } else {
      if (!outcome.trim()) errs.outcome = 'Выбери исход';
    }

    if (isMoneyMode && (!value || isNaN(value) || Number(value) <= 0)) {
      errs.value = MONEY_VALUE_ERROR;
    }
    if (!isMoneyMode && isConfidenceMultiplierEnabled
        && (selectedMultiplier < DEFAULT_BET_MULTIPLIER || selectedMultiplier > maxMultiplier)) {
      errs.multiplier = `Выбери коэффициент от x${DEFAULT_BET_MULTIPLIER} до x${maxMultiplier}`;
    }
    if (Object.keys(errs).length) { setBetErrors(errs); return; }

    const payload = {
      challengeId: Number(challengeId),
    };

    if (isStructured) {
      payload.mainPrediction = {
        homeScore: Number(homeScore),
        awayScore: Number(awayScore),
        outcomeCode: outcomeCode,
      };
      payload.outcome = `${homeScore}-${awayScore} ${outcomeCode}`;
    } else if (isSchemeWinnerOnly) {
      payload.outcome = winnerChoice;
    } else if (isSchemeWinnerWithMethod) {
      payload.outcome = `${winnerChoice}_${winMethodChoice}`;
    } else if (isSchemePlacement) {
      payload.outcome = placementChoice;
    } else if (isSchemeIndividualScore) {
      payload.outcome = `${individualPlacement} (${individualScore})`;
    } else {
      payload.outcome = outcome.trim();
    }

    if (isMoneyMode) {
      payload.value = Number(value);
    } else {
      payload.multiplier = isConfidenceMultiplierEnabled ? selectedMultiplier : DEFAULT_BET_MULTIPLIER;
    }

    setBetLoading(true);
    setBetErrors({});
    try {
      await createBet(roomId, leagueId, challengeId, payload);
      setOutcome('');
      setHomeScore('');
      setAwayScore('');
      setOutcomeCode('');
      setWinnerChoice('');
      setWinMethodChoice('');
      setPlacementChoice('');
      setIndividualPlacement('');
      setIndividualScore('');
      setValue('');
      setMultiplier(String(DEFAULT_BET_MULTIPLIER));
      await loadBets();
    } catch (err) {
      if (isAccessDeniedError(err)) {
        setError(ACCESS_DENIED_MESSAGE);
      } else {
        setBetErrors({ global: err?.response?.data?.message || 'Не удалось создать ставку' });
      }
    } finally {
      setBetLoading(false);
    }
  };

  const handleToggleSideBetSection = () => {
    setSideBetErrors({});
    setSideBetSectionEnabled((enabled) => {
      const nextEnabled = !enabled;
      if (nextEnabled) {
        resetSideBetForm();
      } else {
        closeSideBetForm();
      }
      setSideBetFormOpen(nextEnabled);
      return nextEnabled;
    });
  };

  const handleSubmitSideBet = async (e) => {
    e.preventDefault();
    if (!canBet || !canManageSideBets) {
      setSideBetErrors({ global: 'Особые исходы уже недоступны' });
      return;
    }
    const options = parseSideBetOptions(sideBetOptions);
    const errs = {};
    if (!sideBetTitle.trim()) errs.title = 'Укажи название';
    if (!sideBetReward || isNaN(sideBetReward) || Number(sideBetReward) <= 0) errs.reward = 'Награда должна быть положительным числом';
    if (options.length < 2) errs.options = 'Укажи минимум два варианта';
    if (Object.keys(errs).length) { setSideBetErrors(errs); return; }

    setSideBetLoading(true);
    setSideBetErrors({});
    try {
      const payload = {
        title: sideBetTitle.trim(),
        description: sideBetDescription.trim() || null,
        rewardPoints: Number(sideBetReward),
        options,
      };
      if (sideBetEditingId) {
        await updateSideBet(roomId, leagueId, challengeId, sideBetEditingId, payload);
      } else {
        await createSideBet(roomId, leagueId, challengeId, payload);
      }
      resetSideBetForm();
      await loadSideBets();
      setSideBetSectionEnabled(true);
      setSideBetFormOpen(false);
    } catch (err) {
      setSideBetErrors({ global: err?.response?.data?.message || 'Не удалось сохранить особый исход' });
    } finally {
      setSideBetLoading(false);
    }
  };

  const handleChooseSideBetOption = async (sideBetId, optionId) => {
    if (!canBet) return;
    setSideBetActionId(`${sideBetId}:choice`);
    try {
      await chooseSideBetOption(roomId, leagueId, challengeId, sideBetId, optionId);
      closeSideBetChoiceEdit(sideBetId);
      await loadSideBets();
    } catch (err) {
      setSideBetErrors({ global: err?.response?.data?.message || 'Не удалось выбрать вариант' });
    } finally {
      setSideBetActionId(null);
    }
  };

  const handleDeleteSideBetChoice = async (sideBetId) => {
    if (!canBet) return;
    setSideBetActionId(`${sideBetId}:choice-delete`);
    try {
      await deleteSideBetChoice(roomId, leagueId, challengeId, sideBetId);
      closeSideBetChoiceEdit(sideBetId);
      await loadSideBets();
    } catch (err) {
      setSideBetErrors({ global: err?.response?.data?.message || 'Не удалось удалить решение' });
    } finally {
      setSideBetActionId(null);
    }
  };

  const handleResolveSideBet = async (sideBetId, optionId) => {
    if (!canResolveSideBets) return;
    setSideBetActionId(`${sideBetId}:resolve`);
    try {
      await resolveSideBet(roomId, leagueId, challengeId, sideBetId, optionId);
      await loadSideBets();
      if (isResolved) {
        const lb = await getLeaderboard(roomId, leagueId, challengeId);
        setLeaderboard(Array.isArray(lb) ? lb : []);
      }
    } catch (err) {
      setSideBetErrors({ global: err?.response?.data?.message || 'Не удалось зафиксировать результат особого исхода' });
    } finally {
      setSideBetActionId(null);
    }
  };

  const handleDeleteSideBet = async () => {
    if (!sideBetDeleteTarget || !canBet || !canManageSideBets) return;
    const sideBetId = sideBetDeleteTarget.id;
    setSideBetDeleting(true);
    setSideBetActionId(`${sideBetId}:delete`);
    setSideBetErrors({});
    try {
      await deleteSideBet(roomId, leagueId, challengeId, sideBetId);
      if (String(sideBetEditingId) === String(sideBetId)) {
        closeSideBetForm();
      }
      await loadSideBets();
      setSideBetDeleteTarget(null);
    } catch (err) {
      setSideBetErrors({ global: err?.response?.data?.message || 'Не удалось удалить особый исход' });
    } finally {
      setSideBetDeleting(false);
      setSideBetActionId(null);
    }
  };

  const handleEventResultSubmit = async (e) => {
    e.preventDefault();
    const normalized = eventResult.trim();
    const isStructured = isScoreBasedChallenge(challenge);

    if (isStructured) {
      const validation = validateMainOutcome(
        eventMainResult.homeScore,
        eventMainResult.awayScore,
        eventMainResult.outcomeCode,
        challenge.drawPolicy
      );
      if (!validation.valid) {
        setEventResultError(Object.values(validation.errors).join('. ') || 'Некорректный результат');
        return;
      }
    } else if (!normalized) {
      setEventResultError('Укажи официальный результат события');
      return;
    }

    setEventResultLoading(true);
    setEventResultError('');
    setEventResultSuccess('');
    try {
      const mainResultPayload = isStructured ? {
        homeScore: Number(eventMainResult.homeScore),
        awayScore: Number(eventMainResult.awayScore),
        outcomeCode: eventMainResult.outcomeCode,
      } : null;
      const resultString = isStructured
        ? `${eventMainResult.homeScore}-${eventMainResult.awayScore} ${eventMainResult.outcomeCode}`
        : normalized;
      const updatedEvent = await updateEventResult(event.id, resultString, mainResultPayload);
      setEvent(updatedEvent);
      setEventResult(updatedEvent.result || resultString);
setEventResultSuccess('Результат сохранён. Связанные пари завершаются...');
// Перезагружаем пари и лидерборд после фиксации результата
      const [updatedChallenge, lb] = await Promise.all([
        getChallenge(roomId, leagueId, challengeId),
        getLeaderboard(roomId, leagueId, challengeId).catch(() => []),
      ]);
      setChallenge(updatedChallenge);
      setLeaderboard(Array.isArray(lb) ? lb : []);
      await loadBets();
    } catch (err) {
      setEventResultError(err?.response?.data?.message || 'Не удалось сохранить результат события');
    } finally {
      setEventResultLoading(false);
    }
  };

  const handleDeleteBet = async () => {
    if (!deleteTarget || !isOwnBet(deleteTarget) || !canBet) return;
    setDeleting(true);
    try {
      await deleteBet(roomId, leagueId, challengeId, deleteTarget.id);
      setBets((prev) => prev.filter((b) => b.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setError(accessAwareMessage(err, 'Не удалось удалить ставку'));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteChallenge = async () => {
    setChallengeDeleteConfirmOpen(false);
    setCascadeDeleteConfirmOpen(false);
    try {
      await deleteChallenge(roomId, leagueId, challengeId, challenge.hasBets);
      navigate(buildChallengeListPath(roomId, leagueId));
    } catch (err) {
setError(accessAwareMessage(err, 'Не удалось удалить пари'));
    }
  };

  const handleRecalculateMainScores = async () => {
    if (!canRecalculateMainScores) return;
    setRecalculatingMainScores(true);
    setRecalculateMessage('');
    try {
      const report = await recalculateMainScores(roomId, leagueId, challengeId);
      const [updatedChallenge, updatedBets, updatedLeaderboard] = await Promise.all([
        getChallenge(roomId, leagueId, challengeId),
        getBets(roomId, leagueId, challengeId),
        getLeaderboard(roomId, leagueId, challengeId).catch(() => []),
      ]);
      setChallenge(updatedChallenge);
      setBets(Array.isArray(updatedBets) ? updatedBets : []);
      setLeaderboard(Array.isArray(updatedLeaderboard) ? updatedLeaderboard : []);
      setRecalculateMessage(formatRecalculateMessage(report));
    } catch (err) {
      setError(accessAwareMessage(err, RECALCULATE_ERROR_MESSAGE));
    } finally {
      setRecalculatingMainScores(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)' }}>
      <SkeletonList count={4} />
    </div>
  );

  if (error) return (
    <div className="bm-auth-error">{error}</div>
  );

  return (
    <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)' }}>
      <Breadcrumb
        items={[
          { label: 'Комнаты', to: '/rooms' },
          { label: roomLabel, to: buildRoomPagePath(roomId) },
          ...(isLeagueScope ? [{ label: 'Пари', to: challengeListPath }] : []),
{ label: challenge?.eventTitle || `Пари #${challengeId}` },
        ]}
      />

{/* Hero-блок пари */}
      <Card variant="glass" style={isResolved ? { border: '1px solid rgba(0,214,143,0.3)', background: 'rgba(0,214,143,0.05)' } : {}}>
        <div className="bm-challenge-hero">
          <div className="bm-challenge-hero__main">
            <div className="bm-challenge-hero__title-row">
              <h1 className="bm-h2" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {isResolved && <PiCheckCircleDuotone size={24} style={{ color: 'var(--bm-accent-green)', flexShrink: 0 }} />}
                <span>{challenge?.eventTitle || `Событие #${challenge?.eventId}`}</span>
                {hasOwnBet && (
                  <PiCheckCircleDuotone size={18} style={{ color: 'var(--bm-accent-green)', flexShrink: 0 }} title="Вы сделали основную ставку" />
                )}
              </h1>
              <div className="bm-challenge-hero__bet-count" aria-label={`Ставок: ${bets.length}`}>
                <span className="bm-text-mono" style={{ color: 'var(--bm-accent-primary)' }}>
                  {bets.length}
                </span>
                <span className="bm-text-secondary bm-text-sm">ставок</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Badge status={STATUS_MAP[challenge?.status?.toLowerCase()] || 'primary'} pulse={challenge?.status === 'OPEN' || challenge?.status === 'open'}>
                {challenge?.status?.toLowerCase() || 'open'}
              </Badge>
              {canRecalculateMainScores && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<PiArrowClockwiseDuotone size={18} />}
                  loading={recalculatingMainScores}
                  title={RECALCULATE_BUTTON_TITLE}
                  aria-label={RECALCULATE_BUTTON_TITLE}
                  onClick={handleRecalculateMainScores}
                  style={{
                    width: 38,
                    height: 38,
                    padding: 0,
                    borderRadius: '50%',
                    color: 'var(--bm-accent-primary)',
                  }}
                />
              )}
            </div>

            <div className="bm-challenge-hero__actions">
              {canManageChallenge && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<PiPencilSimpleDuotone size={14} />}
                  onClick={() => navigate(buildChallengeEditPath(roomId, leagueId, challengeId))}
                >
                  Редактировать
                </Button>
              )}
              {challenge?.deletable && !challenge?.hasBets && (
                <Button
                  variant="danger"
                  size="sm"
                  icon={<PiTrashDuotone size={14} />}
                  onClick={() => setChallengeDeleteConfirmOpen(true)}
                >
                  Удалить
                </Button>
              )}
              {challenge?.deletable && challenge?.hasBets && (
                <Button
                  variant="danger"
                  size="sm"
                  icon={<PiTrashDuotone size={14} />}
                  onClick={() => setCascadeDeleteConfirmOpen(true)}
                >
                  Удалить
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {recalculateMessage && <div className="bm-auth-success">{recalculateMessage}</div>}

      {/* Блок связанного события — вторичен, свёрнут по умолчанию */}
      {event && (
        <details className="bm-event-details">
          <summary className="bm-event-details__summary">
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {event.result ? (
                <PiCheckCircleDuotone size={18} style={{ color: 'var(--bm-accent-green)', flexShrink: 0 }} />
              ) : (
                <PiClockCountdownDuotone size={18} style={{ color: 'var(--bm-accent-gold)', flexShrink: 0 }} />
              )}
              <span className="bm-font-medium">Событие: {event.title}</span>
              {(() => {
                const es = resolveEventStatus(event);
                return es && (
                  <Badge
                    status={EVENT_STATUS_BADGE[es]}
                    pulse={es === 'scheduled'}
                  >
                    {EVENT_STATUS_LABELS[es]}
                  </Badge>
                );
              })()}
              {event.result && (
                <span className="bm-text-sm" style={{ color: 'var(--bm-accent-green)', fontWeight: 600 }}>
                  {formatMainOutcome(event.mainResult) || event.result}
                </span>
              )}
              <span className="bm-event-details__date bm-text-sm bm-text-secondary">
                {formatDateRange(event)}
              </span>
            </span>
          </summary>
          <EventBlock
            event={event}
            eventResult={eventResult}
            setEventResult={setEventResult}
            eventMainResult={eventMainResult}
            setEventMainResult={setEventMainResult}
            eventResultError={eventResultError}
            eventResultSuccess={eventResultSuccess}
            eventResultLoading={eventResultLoading}
            onSubmit={handleEventResultSubmit}
            challenge={challenge}
            eventType={eventType}
            eventId={event.id}
          />
        </details>
      )}

      {/* Ожидание результата — для CLOSED */}
      {isClosed && !isResolved && (
        <Card style={{ border: '1px solid rgba(255,209,102,0.35)', background: 'rgba(255,209,102,0.06)' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <PiClockCountdownDuotone size={24} style={{ color: 'var(--bm-accent-gold)', flexShrink: 0 }} />
            <div>
              <h2 className="bm-h4" style={{ marginBottom: 6 }}>
                Ставки закрыты, ждём официальный результат события
              </h2>
              <p className="bm-text-secondary bm-text-sm" style={{ lineHeight: 1.6 }}>
                Новые ставки уже недоступны. Когда создатель ручного события укажет результат на странице события,
это пари автоматически перейдёт в «resolved», а очки и лидерборд пересчитаются.
              </p>
            </div>
          </div>
        </Card>
      )}

      {!canBet && !isResolved && !isClosed && (
        <Card style={{ border: '1px solid rgba(255,209,102,0.35)', background: 'rgba(255,209,102,0.06)' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <PiClockCountdownDuotone size={24} style={{ color: 'var(--bm-accent-gold)', flexShrink: 0 }} />
            <div>
              <h2 className="bm-h4" style={{ marginBottom: 6 }}>
                Ставки недоступны
              </h2>
              <p className="bm-text-secondary bm-text-sm" style={{ lineHeight: 1.6 }}>
                Событие уже началось или дедлайн ставок истёк. Создать, изменить или удалить ставку больше нельзя.
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="bm-challenge-layout">
        {/* Форма ставки — ПЕРВЫЙ приоритет, слева/сверху */}
        {canCreateMainBet && (
          <Card className="bm-challenge-sidebar">
            <h3 className="bm-h4" style={{ marginBottom: 'var(--bm-space-md)' }}>
              Сделать ставку
            </h3>

            {betErrors.global && (
              <div className="bm-auth-error" style={{ marginBottom: 'var(--bm-space-md)' }}>
                {betErrors.global}
              </div>
            )}

            <ScoringRulesBetHint
              scoringRules={challenge?.scoringRules}
              selectedMultiplier={selectedMultiplier}
              maxMultiplier={maxMultiplier}
            />

            <form onSubmit={handleCreateBet} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
              {isSchemeScoreBased || isScoreBasedChallenge(challenge) ? (
                <ScoreBasedOutcomeForm
                  homeScore={homeScore}
                  awayScore={awayScore}
                  outcomeCode={outcomeCode}
                  setHomeScore={setHomeScore}
                  setAwayScore={setAwayScore}
                  setOutcomeCode={setOutcomeCode}
                  drawPolicy={challenge.drawPolicy}
                  errors={betErrors}
                />
              ) : isSchemeWinnerOnly ? (
                <WinnerOnlyOutcomeForm
                  eventType={eventType}
                  winnerChoice={winnerChoice}
                  setWinnerChoice={setWinnerChoice}
                  eventOutcomes={challenge?.eventOutcomes}
                  errors={betErrors}
                />
              ) : isSchemeWinnerWithMethod ? (
                <WinnerWithMethodOutcomeForm
                  eventType={eventType}
                  winnerChoice={winnerChoice}
                  winMethodChoice={winMethodChoice}
                  setWinnerChoice={setWinnerChoice}
                  setWinMethodChoice={setWinMethodChoice}
                  eventOutcomes={challenge?.eventOutcomes}
                  errors={betErrors}
                />
              ) : isSchemePlacement ? (
                <PlacementOutcomeForm
                  eventType={eventType}
                  placementChoice={placementChoice}
                  setPlacementChoice={setPlacementChoice}
                  eventOutcomes={challenge?.eventOutcomes}
                  errors={betErrors}
                />
              ) : isSchemeIndividualScore ? (
                <IndividualScoreOutcomeForm
                  eventType={eventType}
                  individualPlacement={individualPlacement}
                  individualScore={individualScore}
                  setIndividualPlacement={setIndividualPlacement}
                  setIndividualScore={setIndividualScore}
                  eventOutcomes={challenge?.eventOutcomes}
                  errors={betErrors}
                />
              ) : (
                <div className="bm-input-group">
                  <span className="bm-label">Исход *</span>
                  {(challenge?.eventOutcomes?.length > 0) ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {challenge.eventOutcomes.map((chip) => (
                        <button
                          key={chip}
                          type="button"
                          className={`bm-btn bm-btn--sm ${outcome === chip ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
                          onClick={() => setOutcome(chip)}
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <Input
                    type="text"
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    placeholder={challenge?.eventOutcomes?.length > 0 ? 'Или введи свой вариант' : 'Введи исход'}
                    error={betErrors.outcome}
                    style={{ marginTop: challenge?.eventOutcomes?.length > 0 ? 8 : 0 }}
                  />
                </div>
              )}

              {isMoneyMode ? (
                <Input
                  label="Сумма ставки (₽) *"
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="100"
                  min="1"
                  error={betErrors.value}
                />
              ) : isConfidenceMultiplierEnabled ? (
                <div className="bm-input-group">
                  <span className="bm-label">Коэффициент уверенности</span>
                  <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}>Коэффициент влияет на итоговые очки за ставку</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {multiplierOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`bm-btn bm-btn--sm ${selectedMultiplier === option ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
                        onClick={() => setMultiplier(String(option))}
                      >
                        x{option}
                      </button>
                    ))}
                  </div>
                  {betErrors.multiplier && <span className="bm-input-error">{betErrors.multiplier}</span>}
                </div>
              ) : null}

              <Button
                type="submit"
                variant="success"
                size="md"
                loading={betLoading}
                style={{ width: '100%' }}
              >
                Поставить
              </Button>
            </form>
          </Card>
        )}

        {/* Итоги для завершённых — слева/сверху */}
        {isResolved && (
          <Card className="bm-challenge-sidebar" style={{ border: '1px solid rgba(0,214,143,0.2)' }}>
            <h3 className="bm-h4" style={{ marginBottom: 'var(--bm-space-md)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <PiCheckCircleDuotone size={20} style={{ color: 'var(--bm-accent-green)' }} />
              Итоги
            </h3>
            <div style={{ display: 'grid', gap: 'var(--bm-space-sm)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'baseline', gap: 'var(--bm-space-sm)' }}>
                <div className="bm-text-secondary bm-text-sm">Официальный исход</div>
                <div className="bm-font-medium" style={{ color: 'var(--bm-accent-green)', textAlign: 'right', overflowWrap: 'anywhere' }}>
                  {formatMainOutcome(challenge?.mainResult) || challenge?.outcome || '—'}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'baseline', gap: 'var(--bm-space-sm)' }}>
                <div className="bm-text-secondary bm-text-sm">Всего ставок</div>
                <div className="bm-text-mono" style={{ textAlign: 'right' }}>{bets.length}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'baseline', gap: 'var(--bm-space-sm)' }}>
                <div className="bm-text-secondary bm-text-sm">Угадали</div>
                <div className="bm-text-mono" style={{ color: 'var(--bm-accent-green)', textAlign: 'right' }}>
                  {bets.filter(b => b.score > 0).length}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'baseline', gap: 'var(--bm-space-sm)' }}>
                <div className="bm-text-secondary bm-text-sm">Очки за особые исходы</div>
                <div className="bm-text-mono" style={{ color: sideBetScore > 0 ? 'var(--bm-accent-green)' : 'var(--bm-text-muted)', textAlign: 'right' }}>
                  {sideBetScore}
                </div>
              </div>
              {(challenge?.currentUserBreakdown || sideBetScore > 0) && (
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'baseline', gap: 'var(--bm-space-sm)' }}>
                  <div className="bm-text-secondary bm-text-sm">Ваш результат</div>
                  <div style={{ textAlign: 'right' }}>
                    {totalUserScore(challenge.currentUserBreakdown, sideBetScore)} очков
                  </div>
                  {challenge?.currentUserBreakdown && (
                    <div className="bm-text-secondary bm-text-sm" style={{ gridColumn: '1 / -1', textAlign: 'right' }}>
                      {formatBreakdownText(challenge.currentUserBreakdown)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Список ставок — справа/снизу */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
          <h2 className="bm-h3">{isResolved ? 'История ставок' : 'Ставки'}</h2>
          {bets.length === 0 ? (
            <EmptyState
              icon={isMoneyMode ? <PiCurrencyCircleDollarDuotone size={36} /> : <PiTargetDuotone size={36} />}
              title="Ставок пока нет"
              hint={canBet ? 'Сделай первую ставку в форме слева' : 'Ставки не были сделаны'}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {bets.map((bet) => {
                const isHidden = isBetValueHidden(bet);
                const isOwn = isOwnBet(bet);
                const leaderboardEntry = findLeaderboardEntry(leaderboard, bet.userId);
                const resolvedBetScore = getResolvedBetScore(bet, leaderboardEntry);
                const resolvedBetBreakdown = formatBetScoreBreakdown(bet, leaderboardEntry);
                const isWinner = isResolved && !isHidden && resolvedBetScore > 0;
                const canDeleteBet = canBet && !isHidden && isOwn;
                const betStakeLabel = formatBetStake(bet);
                const cardClasses = [
                  'bm-bet-card',
                  isOwn ? 'bm-bet-card--own' : '',
                  isWinner ? 'bm-bet-card--winner' : '',
                ].filter(Boolean).join(' ');
                const iconClasses = [
                  'bm-bet-card__icon',
                  isOwn ? 'bm-bet-card__icon--own' : '',
                  isWinner ? 'bm-bet-card__icon--winner' : '',
                ].filter(Boolean).join(' ');
                return (
                  <Card
                    key={bet.id}
                    className={cardClasses}
                    style={isResolved ? { opacity: isWinner || isOwn ? 1 : 0.6 } : {}}
                  >
                    <div className={iconClasses}>
                      {isWinner ? <PiTrophyDuotone size={18} /> : betIcon()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="bm-bet-card__author-row">
                        <p className="bm-font-medium bm-truncate">{bet.authorName || `Участник #${bet.userId}`}</p>
                        {isOwn && <span className="bm-bet-card__own-badge">Моя ставка</span>}
                      </div>
                      <p className="bm-text-sm bm-truncate bm-bet-card__outcome">
                        {isHidden ? 'Ставка сделана, значение скрыто' : (formatMainOutcome(bet.mainPrediction) || bet.outcome)}
                      </p>
                      {(isHidden || betStakeLabel) && (
                        <p className={`bm-text-sm ${isHidden ? 'bm-bet-card__hidden-value' : 'bm-bet-card__amount'}`}>
                          {isHidden ? BET_HIDDEN_VALUE_MESSAGE : betStakeLabel}
                        </p>
                      )}
                    </div>
                    {isResolved && (
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: 'var(--bm-text-md)',
                            color: resolvedBetScore > 0 ? 'var(--bm-accent-green)' : 'var(--bm-text-muted)',
                          }}
                        >
                          Итого: {resolvedBetScore} очк.
                        </span>
                        {resolvedBetBreakdown && (
                          <div
                            className="bm-text-sm"
                            style={{ color: 'var(--bm-text-muted)', marginTop: 2 }}
                          >
                            {resolvedBetBreakdown}
                          </div>
                        )}
                        {bet.breakdown && (!isHidden || isOwn) && (
                          <div
                            className="bm-text-sm"
                            style={{ color: 'var(--bm-text-muted)', marginTop: 2 }}
                          >
                            {formatBreakdownText(bet.breakdown)}
                          </div>
                        )}
                      </div>
                    )}
                    {canDeleteBet && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<PiTrashDuotone size={14} />}
                        onClick={() => setDeleteTarget(bet)}
                        style={{ color: 'var(--bm-accent-red)', flexShrink: 0 }}
                      />
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </section>


      </div>

      {/* Особые исходы */}
      {shouldRenderSideBetSection && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--bm-space-md)', flexWrap: 'wrap' }}>
          <h2 className="bm-h3" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
            <PiTargetDuotone size={22} style={{ color: 'var(--bm-accent-primary)' }} /> Особые исходы
          </h2>
          {canBet && canManageSideBets && shouldShowSideBetDetails && (
            <Button
              variant={sideBetFormOpen ? 'secondary' : 'primary'}
              size="sm"
              onClick={() => {
                if (sideBetFormOpen) {
                  closeSideBetForm();
                } else {
                  openCreateSideBetForm();
                }
              }}
            >
              {sideBetFormOpen ? 'Скрыть форму' : 'Добавить'}
            </Button>
          )}
        </div>

        {canToggleSideBetDetails && (
          <label
            className="bm-text-sm"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              border: sideBetSectionEnabled ? '1px solid rgba(108,99,255,0.32)' : '1px solid var(--bm-border)',
              borderRadius: 'var(--bm-radius-lg)',
              background: sideBetSectionEnabled ? 'rgba(108,99,255,0.06)' : 'var(--bm-bg-elevated)',
              cursor: 'pointer',
              transition: 'border-color 160ms ease, background 160ms ease, box-shadow 160ms ease',
              boxShadow: sideBetSectionEnabled ? '0 0 0 3px rgba(108,99,255,0.06)' : 'none',
            }}
          >
            <input
              type="checkbox"
              checked={sideBetSectionEnabled}
              onChange={handleToggleSideBetSection}
              style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
            />
            <span
              aria-hidden="true"
              style={{
                width: 42,
                height: 24,
                padding: 2,
                border: sideBetSectionEnabled ? '1px solid var(--bm-accent-primary)' : '1px solid rgba(255,255,255,0.34)',
                borderRadius: 999,
                background: sideBetSectionEnabled ? 'var(--bm-accent-primary)' : 'rgba(255,255,255,0.16)',
                transition: 'background 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
                boxShadow: sideBetSectionEnabled ? '0 0 0 3px rgba(108,99,255,0.12)' : 'inset 0 0 0 1px rgba(0,0,0,0.18)',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  display: 'block',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: '#ffffff',
                  transform: sideBetSectionEnabled ? 'translateX(18px)' : 'translateX(0)',
                  transition: 'transform 160ms ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.36)',
                }}
              />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="bm-font-medium">Включить особые исходы</span>
              <br />
              <span className="bm-text-secondary">
Это необязательный дополнительный прогноз — включай только если он нужен для пари.
              </span>
            </span>
          </label>
        )}

        {sideBetErrors.global && shouldShowSideBetDetails && (
          <div className="bm-auth-error">{sideBetErrors.global}</div>
        )}
        {sideBetFormOpen && canBet && canManageSideBets && shouldShowSideBetDetails && (
          <Card style={{ border: '1px solid rgba(108,99,255,0.25)' }}>
            <h3 className="bm-h4" style={{ marginBottom: 'var(--bm-space-md)' }}>
              {editingSideBet ? 'Редактировать особый исход' : 'Новый особый исход'}
            </h3>
            <form onSubmit={handleSubmitSideBet} style={{ display: 'grid', gap: 'var(--bm-space-md)' }}>
              <Input
                label="Название *"
                type="text"
                value={sideBetTitle}
                onChange={(e) => setSideBetTitle(e.target.value)}
                placeholder="Обе команды забьют"
                error={sideBetErrors.title}
              />
              <Input
                label="Описание"
                type="text"
                value={sideBetDescription}
                onChange={(e) => setSideBetDescription(e.target.value)}
                placeholder="Необязательное условие"
              />
              <Input
                label="Награда в очках *"
                type="number"
                value={sideBetReward}
                onChange={(e) => setSideBetReward(e.target.value)}
                min="1"
                error={sideBetErrors.reward}
              />
              <div className="bm-input-group">
                <span className="bm-label">Варианты, каждый с новой строки *</span>
                <textarea
                  className={`bm-input ${sideBetErrors.options ? 'bm-input--error' : ''}`}
                  value={sideBetOptions}
                  onChange={(e) => setSideBetOptions(e.target.value)}
                  rows={4}
                  disabled={sideBetOptionsLocked}
                />
                {sideBetOptionsLocked && (
                  <span className="bm-text-secondary bm-text-sm">
                    Варианты нельзя изменить: участники уже сделали выбор. Можно исправить название, описание и награду.
                  </span>
                )}
                {sideBetErrors.options && <span className="bm-input-error">{sideBetErrors.options}</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={closeSideBetForm}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  loading={sideBetLoading}
                  icon={editingSideBet ? <PiPencilSimpleDuotone size={16} /> : null}
                >
                  {editingSideBet ? 'Сохранить изменения' : 'Создать особый исход'}
                </Button>
              </div>
            </form>
          </Card>
        )}
        {shouldShowSideBetEmptyState && (
          <EmptyState
            icon={<PiTargetDuotone size={36} />}
            title="Особых исходов пока нет"
            hint={canBet ? 'Можно добавить дополнительный прогноз через кнопку выше' : 'Дополнительные прогнозы не создавались'}
          />
        )}
        {sideBets.length > 0 && (
          <div style={{ display: 'grid', gap: 10 }}>
            {sideBets.map((sideBet) => {
              const resolved = sideBet.status === 'RESOLVED' || sideBet.status === 'resolved';
              const choicesCount = Number(sideBet.choicesCount || 0);
              const hasCurrentUserChoice = Boolean(sideBet.currentUserOptionId);
              const editingChoice = isSideBetChoiceEditing(sideBet.id);
              const visibleOptions = sideBetOptionsForDisplay(sideBet, resolved, editingChoice);
              const canManageDraftSideBet = canBet && canManageSideBets && !resolved;
              const canDeleteDraftSideBet = canManageDraftSideBet && choicesCount === 0;
              return (
                <Card key={sideBet.id} style={resolved ? { border: '1px solid rgba(0,214,143,0.25)' } : {}}>
                  <div className="bm-side-bet-card__header">
                    <div className="bm-side-bet-card__content">
                      <h3 className="bm-h4" style={{ marginBottom: 4 }}>{sideBet.title}</h3>
                      {sideBet.description && <p className="bm-text-secondary bm-text-sm">{sideBet.description}</p>}
                    </div>
                    <div className="bm-side-bet-card__meta">
                      <Badge status={resolved ? 'resolved' : 'open'}>{resolved ? 'resolved' : 'open'}</Badge>
                      <div className="bm-side-bet-card__reward">
                        +{sideBet.rewardPoints} очк.
                      </div>
                      {canManageDraftSideBet && (
                        <div className="bm-side-bet-card__actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<PiPencilSimpleDuotone size={14} />}
                            onClick={() => openEditSideBetForm(sideBet)}
                          >
                            Изменить
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<PiTrashDuotone size={14} />}
                            disabled={!canDeleteDraftSideBet}
                            loading={sideBetActionId === `${sideBet.id}:delete`}
                            title={canDeleteDraftSideBet ? 'Удалить особый исход' : 'Нельзя удалить исход с выбором участников'}
                            onClick={() => setSideBetDeleteTarget(sideBet)}
                            style={{ color: canDeleteDraftSideBet ? 'var(--bm-accent-red)' : 'var(--bm-text-muted)' }}
                          >
                            Удалить
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {visibleOptions.map((option) => {
                      const selected = String(sideBet.currentUserOptionId || '') === String(option.id);
                      const winning = String(sideBet.winningOptionId || '') === String(option.id);
                      return (
                        <button
                          key={option.id}
                          type="button"
                          className={`bm-btn bm-btn--sm ${selected || winning ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
                          disabled={!canBet || resolved || sideBetActionId === `${sideBet.id}:choice`}
                          onClick={() => handleChooseSideBetOption(sideBet.id, option.id)}
                        >
                          {formatSideBetOptionLabel(option, selected, winning, resolved)}
                        </button>
                      );
                    })}
                  </div>
                  {resolved && (
                    <div className="bm-text-secondary bm-text-sm" style={{ marginTop: 8 }}>
                      Правильный вариант: {winningSideBetOptionLabel(sideBet)}
                      {hasCurrentUserChoice ? ` · твой выбор: ${currentUserSideBetOptionLabel(sideBet)}` : ' · ты не выбирал вариант'}
                      {` · твои очки: ${sideBet.currentUserScore || 0}`}
                    </div>
                  )}
                  {canBet && !resolved && hasCurrentUserChoice && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => toggleSideBetChoiceEdit(sideBet.id)}
                      >
                        {editingChoice ? 'Скрыть варианты' : 'Изменить решение'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={sideBetActionId === `${sideBet.id}:choice-delete`}
                        onClick={() => handleDeleteSideBetChoice(sideBet.id)}
                        style={{ color: 'var(--bm-accent-red)' }}
                      >
                        Удалить решение
                      </Button>
                    </div>
                  )}
                  {canResolveSideBets && !resolved && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end', marginTop: 12 }}>
                      {(sideBet.options || []).map((option) => (
                        <Button
                          key={`resolve-${option.id}`}
                          variant="ghost"
                          size="sm"
                          loading={sideBetActionId === `${sideBet.id}:resolve`}
                          onClick={() => handleResolveSideBet(sideBet.id, option.id)}
                        >
                          Resolve: {option.label}
                        </Button>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
        </section>
      )}

      <Modal
        isOpen={!!sideBetDetailsTarget}
        onClose={() => setSideBetDetailsTarget(null)}
        title={sideBetDetailsTarget ? `Участники: ${sideBetDetailsTarget.title}` : 'Участники особого исхода'}
        size="lg"
      >
        {sideBetDetailsTarget && (
          <div className="bm-side-bet-details">
            <div className="bm-side-bet-details__summary">
              <strong>{sideBetDetailsTarget.choicesCount || 0}</strong>
              <span>выборов всего</span>
              {sideBetDetailsTarget.currentUserOptionId && (
                <span>твой выбор: {currentUserSideBetOptionLabel(sideBetDetailsTarget)}</span>
              )}
              {(sideBetDetailsTarget.status === 'RESOLVED' || sideBetDetailsTarget.status === 'resolved') && (
                <span>твои очки: {sideBetDetailsTarget.currentUserScore || 0}</span>
              )}
            </div>
            {(sideBetDetailsTarget.options || []).map((option) => {
              const participants = option.participants || [];
              const winning = String(sideBetDetailsTarget.winningOptionId || '') === String(option.id);
              return (
                <div key={`details-${option.id}`} className="bm-side-bet-details__option">
                  <div className="bm-side-bet-details__option-header">
                    <div>
                      <strong>{option.label}</strong>
                      {winning && <span className="bm-side-bet-details__winner">Победил</span>}
                    </div>
                    <span className="bm-text-secondary bm-text-sm">{option.choicesCount || 0} выборов</span>
                  </div>
                  {participants.length > 0 ? (
                    <div className="bm-side-bet-details__participants">
                      {participants.map((participant, index) => (
                        <div key={sideBetParticipantKey(participant, index)} className="bm-side-bet-details__participant">
                          <span>
                            {participant.displayName || `Участник #${participant.userId}`}
                            {participant.currentUser && <em>ты</em>}
                          </span>
                          {(sideBetDetailsTarget.status === 'RESOLVED' || sideBetDetailsTarget.status === 'resolved') && (
                            <strong>{participant.score || 0} очк.</strong>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="bm-text-secondary bm-text-sm">{SIDE_BET_DETAILS_EMPTY_MESSAGE}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal>
      <ConfirmModal
        isOpen={!!deleteTarget && isOwnBet(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteBet}
        loading={deleting}
        title="Удалить ставку"
        message={deleteBetMessage(deleteTarget)}
      />
      <ConfirmModal
        isOpen={!!sideBetDeleteTarget}
        onClose={() => setSideBetDeleteTarget(null)}
        onConfirm={handleDeleteSideBet}
        loading={sideBetDeleting}
        title="Удалить особый исход"
        message={`Удалить особый исход «${sideBetDeleteTarget?.title || ''}»? Это действие нельзя отменить.`}
      />
      <ConfirmModal
        isOpen={challengeDeleteConfirmOpen}
        onClose={() => setChallengeDeleteConfirmOpen(false)}
        onConfirm={handleDeleteChallenge}
title="Удалить пари?"
        message="Это действие необратимо. Пари будет удалено навсегда."
        confirmLabel="Удалить"
      />
      <ConfirmModal
        isOpen={cascadeDeleteConfirmOpen}
        onClose={() => setCascadeDeleteConfirmOpen(false)}
        onConfirm={handleDeleteChallenge}
title="Удалить пари со ставками?"
        message="У этого пари есть ставки. При удалении все ставки и особые исходы будут удалены каскадно. Это действие необратимо."
        confirmLabel="Удалить всё"
      />
    </div>
  );

  function formatRecalculateMessage(report) {
    if (!report) return RECALCULATE_SUCCESS_MESSAGE;
    return `${RECALCULATE_SUCCESS_MESSAGE}: изменено ставок ${report.changedBetCount ?? 0}, дельта ${report.scoreDelta ?? 0}`;
  }

  function ScoringRulesBetHint({ scoringRules }) {
    const visibleRules = getVisibleScoringRules(scoringRules);

    if (visibleRules.length === 0) return null;

    if (visibleRules.length === 1) {
      const rule = visibleRules[0];
      return (
        <div className="bm-bet-scoring-hint" aria-label="Очки этой ставки">
          <div className="bm-bet-scoring-hint__title">Очки этой ставки</div>
          <p className="bm-bet-scoring-hint__text">
            {rule.label}: {formatPoints(rule.points)}
          </p>
        </div>
      );
    }

    return (
      <details className="bm-bet-scoring-hint">
        <summary className="bm-bet-scoring-hint__summary">Как считаются очки этой ставки</summary>
        <div className="bm-bet-scoring-hint__rules">
          {visibleRules.map((rule) => (
            <div key={rule.type} className="bm-bet-scoring-hint__rule">
              <span>{rule.label}: {formatPoints(rule.points)}</span>
            </div>
          ))}
        </div>
      </details>
    );
  }

  function openCreateSideBetForm() {
    resetSideBetForm();
    setSideBetErrors({});
    setSideBetFormOpen(true);
    setSideBetSectionEnabled(true);
  }

  function openEditSideBetForm(sideBet) {
    setSideBetErrors({});
    setSideBetEditingId(sideBet.id);
    setSideBetTitle(sideBet.title || '');
    setSideBetDescription(sideBet.description || '');
    setSideBetReward(String(sideBet.rewardPoints || 1));
    setSideBetOptions((sideBet.options || []).map((option) => option.label).join('\n'));
    setSideBetFormOpen(true);
    setSideBetSectionEnabled(true);
  }

  function closeSideBetForm() {
    resetSideBetForm();
    setSideBetErrors({});
    setSideBetFormOpen(false);
  }

  function resetSideBetForm() {
    setSideBetEditingId(null);
    setSideBetTitle('');
    setSideBetDescription('');
    setSideBetReward('1');
    setSideBetOptions(DEFAULT_SIDE_BET_OPTIONS.join('\n'));
  }

  function parseSideBetOptions(value) {
    return value
      .split('\n')
      .map((option) => option.trim())
      .filter(Boolean);
  }

  function isSideBetChoiceEditing(sideBetId) {
    return sideBetChoiceEditingIds.some((editingId) => String(editingId) === String(sideBetId));
  }

  function toggleSideBetChoiceEdit(sideBetId) {
    setSideBetChoiceEditingIds((editingIds) => (
      editingIds.some((editingId) => String(editingId) === String(sideBetId))
        ? editingIds.filter((editingId) => String(editingId) !== String(sideBetId))
        : [...editingIds, sideBetId]
    ));
  }

  function closeSideBetChoiceEdit(sideBetId) {
    setSideBetChoiceEditingIds((editingIds) => (
      editingIds.filter((editingId) => String(editingId) !== String(sideBetId))
    ));
  }

  function sideBetOptionsForDisplay(sideBet, resolved, editingChoice) {
    const options = sideBet?.options || [];
    if (resolved || !sideBet?.currentUserOptionId || editingChoice) return options;
    return options.filter((option) => String(option.id) === String(sideBet.currentUserOptionId));
  }

  function formatSideBetOptionLabel(option, selected, winning, resolved) {
    if (!resolved) return option.label;
    return winning ? `${option.label} · правильный` : option.label;
  }

  function winningSideBetOptionLabel(sideBet) {
    return (sideBet?.options || []).find((option) => String(option.id) === String(sideBet.winningOptionId))?.label || '—';
  }

  function currentUserSideBetOptionLabel(sideBet) {
    return (sideBet?.options || []).find((option) => String(option.id) === String(sideBet.currentUserOptionId))?.label || '—';
  }

  function sideBetParticipantKey(participant, index) {
    return `${participant.userId || 'unknown'}-${index}`;
  }

  function deleteBetMessage(bet) {
    if (!bet) return 'Удалить ставку?';
    const stakeLabel = formatBetStake(bet);
    const stakeSuffix = stakeLabel ? ` (${stakeLabel})` : '';
    return `Удалить ставку «${bet.outcome || 'скрыто'}»${stakeSuffix}?`;
  }

  function formatBetStake(bet) {
    if (isMoneyMode) {
      return `${bet?.value ?? '—'} ₽`;
    }
    if (!isConfidenceMultiplierEnabled) {
      return null;
    }
    const betMultiplier = positiveInteger(bet?.multiplier, DEFAULT_BET_MULTIPLIER);
    return `коэффициент x${betMultiplier}`;
  }

  function betIcon() {
    return isMoneyMode ? <PiCurrencyCircleDollarDuotone size={18} /> : <PiTargetDuotone size={18} />;
  }

  function positiveInteger(value, fallback) {
    const number = Number(value);
    return Number.isInteger(number) && number >= 1 ? number : fallback;
  }

  function isOwnBet(bet) {
    const betUserId = bet?.userId ?? bet?.creatorId;
    return currentUserId != null && betUserId != null && String(betUserId) === String(currentUserId);
  }

  function isBetValueHidden(bet) {
    return bet?.valueHidden === true;
  }
}

// ===================================================================================================================
// = ScoreBasedOutcomeForm — форма structured счёта и исхода
// ===================================================================================================================

function ScoreBasedOutcomeForm({
  homeScore,
  awayScore,
  outcomeCode,
  setHomeScore,
  setAwayScore,
  setOutcomeCode,
  drawPolicy,
  errors,
}) {
  // Определяем допустимые исходы для текущего счёта
  const currentOutcomeOptions = (homeScore !== '' && awayScore !== '')
    ? outcomeCodesForScore(homeScore, awayScore, drawPolicy)
    : allowedOutcomeCodes(drawPolicy);

  const isDrawForbidden = drawPolicy === DrawPolicy.DRAW_FORBIDDEN;
  const isDrawWithTieBreak = drawPolicy === DrawPolicy.DRAW_WITH_TIE_BREAK_WINNER;
  const isDrawn = homeScore !== '' && awayScore !== '' && Number(homeScore) === Number(awayScore);

  // Автоселект исхода если только один вариант
  React.useEffect(() => {
    if (currentOutcomeOptions.length === 1 && outcomeCode !== currentOutcomeOptions[0]) {
      setOutcomeCode(currentOutcomeOptions[0]);
    }
    // Если текущий outcomeCode больше не допустим — сбросить
    if (outcomeCode && !currentOutcomeOptions.includes(outcomeCode)) {
      setOutcomeCode(currentOutcomeOptions.length === 1 ? currentOutcomeOptions[0] : '');
    }
  }, [currentOutcomeOptions.join(',')]);

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--bm-space-md)', alignItems: 'flex-end' }}>
        <Input
          label="Счёт 1-й стороны *"
          type="number"
          value={homeScore}
          onChange={(e) => setHomeScore(e.target.value)}
          placeholder="0"
          min="0"
          error={errors.homeScore}
          style={{ flex: 1 }}
        />
        <span className="bm-label" style={{ paddingBottom: 10 }}>-</span>
        <Input
          label="Счёт 2-й стороны *"
          type="number"
          value={awayScore}
          onChange={(e) => setAwayScore(e.target.value)}
          placeholder="0"
          min="0"
          error={errors.awayScore}
          style={{ flex: 1 }}
        />
      </div>

      {isDrawn && isDrawForbidden && (
        <div style={{ color: 'var(--bm-accent-red)', fontWeight: 600, fontSize: 'var(--bm-text-sm)' }}>
Ничейный счёт запрещён для этого пари. Измени счёт, чтобы одна сторона победила.
        </div>
      )}

      {isDrawn && isDrawWithTieBreak && (
        <div className="bm-text-sm bm-text-secondary">
          Счёт равный — выбери победителя по тай-брейку:
        </div>
      )}

      <div className="bm-input-group">
        <span className="bm-label">Код исхода *</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {currentOutcomeOptions.map((code) => (
            <button
              key={code}
              type="button"
              className={`bm-btn bm-btn--sm ${outcomeCode === code ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
              onClick={() => setOutcomeCode(code)}
            >
              {outcomeCodeShortLabel(code)}
            </button>
          ))}
        </div>
        {errors.outcomeCode && <span className="bm-input-error">{errors.outcomeCode}</span>}
      </div>

      {/* Preview ставки */}
      {homeScore !== '' && awayScore !== '' && outcomeCode && (
        <div style={{
          padding: '8px 12px',
          borderRadius: 'var(--bm-radius-lg)',
          background: 'var(--bm-bg-elevated)',
          fontSize: 'var(--bm-text-sm)',
          color: 'var(--bm-accent-primary)',
          fontWeight: 600,
        }}>
          Твой прогноз: {homeScore}-{awayScore} {outcomeCode}
        </div>
      )}
    </>
  );
}

// ===================================================================================================================
// = EventBlock — блок связанного события на странице пари
// ===================================================================================================================

const EVENT_SOURCE_EXTERNAL = 'EXTERNAL';
const EVENT_SOURCE_MANUAL = 'MANUAL';

function EventBlock({
  event,
  eventResult,
  setEventResult,
  eventMainResult,
  setEventMainResult,
  eventResultError,
  eventResultSuccess,
  eventResultLoading,
  onSubmit,
  challenge,
  eventType,
  eventId,
}) {
  const isExternal = event.source === EVENT_SOURCE_EXTERNAL;
  const canFixResult = event.resultEditable === true;
  const isStructured = isScoreBasedChallenge(challenge);
  const outcomeScheme = eventType?.outcomeScheme || null;
  const isSchemeScoreBased = outcomeScheme === OutcomeScheme.SCORE_BASED;
  const isSchemeWinnerOnly = outcomeScheme === OutcomeScheme.WINNER_ONLY;
  const isSchemeWinnerWithMethod = outcomeScheme === OutcomeScheme.WINNER_WITH_METHOD;
  const isSchemePlacement = outcomeScheme === OutcomeScheme.PLACEMENT;
  const isSchemeIndividualScore = outcomeScheme === OutcomeScheme.INDIVIDUAL_SCORE;
  const eventStatus = resolveEventStatus(event);
  const eventDateRange = formatDateRange(event);

  return (
    <Card style={{ border: 'none', borderRadius: 0 }}>
      {/* Метаданные события */}
      <div className="bm-event-block__meta" style={{ marginBottom: 'var(--bm-space-md)' }}>
        {eventDateRange && (
          <div className="bm-event-block__row" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <PiCalendarDuotone size={15} style={{ color: 'var(--bm-accent-primary)', flexShrink: 0 }} />
            <span className="bm-text-sm">{eventDateRange}</span>
          </div>
        )}
        {eventStatus && (
          <div className="bm-event-block__row" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <PiClockDuotone size={15} style={{ color: 'var(--bm-accent-primary)', flexShrink: 0 }} />
            <Badge status={EVENT_STATUS_BADGE[eventStatus]} pulse={eventStatus === 'scheduled'}>
              {EVENT_STATUS_LABELS[eventStatus]}
            </Badge>
          </div>
        )}
        {event.description && (
          <div className="bm-event-block__row bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>
            {event.description}
          </div>
        )}
        {eventId && (
          <div style={{ marginTop: 'var(--bm-space-sm)' }}>
            <a
              href={`/events/${eventId}`}
              className="bm-event-block__link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--bm-text-sm)', fontWeight: 600, color: 'var(--bm-accent-primary)', textDecoration: 'none' }}
            >
              <PiArrowSquareOutDuotone size={15} />
              Перейти к событию
            </a>
          </div>
        )}
      </div>

      {isExternal && !canFixResult && (
        <p className="bm-text-secondary bm-text-sm" style={{ marginBottom: 'var(--bm-space-sm)' }}>
          Внешнее событие — результат синхронизируется автоматически.
        </p>
      )}
      {!canFixResult && !isExternal && (
        <p className="bm-text-secondary bm-text-sm" style={{ marginBottom: 'var(--bm-space-sm)' }}>
          Завершить событие может только его создатель.
        </p>
      )}

      {/* Форма фиксации результата — только для владельца ручного события */}
      {canFixResult && (
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
          {isStructured || isSchemeScoreBased ? (
            <ScoreBasedOutcomeForm
              homeScore={eventMainResult.homeScore}
              awayScore={eventMainResult.awayScore}
              outcomeCode={eventMainResult.outcomeCode}
              setHomeScore={(v) => setEventMainResult((prev) => ({ ...prev, homeScore: v }))}
              setAwayScore={(v) => setEventMainResult((prev) => ({ ...prev, awayScore: v }))}
              setOutcomeCode={(v) => setEventMainResult((prev) => ({ ...prev, outcomeCode: v }))}
              drawPolicy={challenge.drawPolicy}
              errors={eventResultError ? { global: eventResultError } : {}}
            />
          ) : isSchemeWinnerOnly || isSchemeWinnerWithMethod || isSchemePlacement || isSchemeIndividualScore ? (
            <OutcomeSchemeResultForm
              eventType={eventType}
              outcomeScheme={outcomeScheme}
              eventResult={eventResult}
              setEventResult={setEventResult}
              eventOutcomes={event.outcomes}
            />
          ) : (
            <>
              {(event.outcomes?.length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {event.outcomes.map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      className={`bm-btn bm-btn--sm ${eventResult === chip ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
                      onClick={() => setEventResult(chip)}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              )}
              <input
                className={`bm-input${eventResultError ? ' bm-input--error' : ''}`}
                type="text"
                value={eventResult}
                onChange={(e) => setEventResult(e.target.value)}
                placeholder={
                  event.outcomes?.length > 0
                    ? 'Выбери исход выше или введи точный результат'
                    : 'Например: Победа команды А'
                }
              />
            </>
          )}
          {eventResultError && !isStructured && (
            <span className="bm-input-error">{eventResultError}</span>
          )}
          {eventResultSuccess && (
            <div style={{ color: 'var(--bm-accent-green)', fontWeight: 600 }}>
              {eventResultSuccess}
            </div>
          )}
          <Button
            type="submit"
            variant="success"
            loading={eventResultLoading}
            icon={<PiCheckCircleDuotone size={16} />}
          >
Сохранить результат и завершить пари
          </Button>
        </form>
      )}
    </Card>
  );
}

// ===================================================================================================================
// = OutcomeScheme-specific bet forms
// ===================================================================================================================

function WinnerOnlyOutcomeForm({ eventType, winnerChoice, setWinnerChoice, eventOutcomes, errors }) {
  const outcomes = eventOutcomes?.length > 0
    ? eventOutcomes
    : (eventType ? generateOutcomesFromEventType(eventType) : []);

  return (
    <div className="bm-input-group">
      <span className="bm-label">
        {outcomeSchemeIcon(OutcomeScheme.WINNER_ONLY)} Победитель *
      </span>
      {outcomes.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {outcomes.map((chip) => (
            <button
              key={chip}
              type="button"
              className={`bm-btn bm-btn--sm ${winnerChoice === chip ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
              onClick={() => setWinnerChoice(chip)}
            >
              {chip}
            </button>
          ))}
        </div>
      ) : (
        <Input
          type="text"
          value={winnerChoice}
          onChange={(e) => setWinnerChoice(e.target.value)}
          placeholder="Укажи победителя"
          error={errors.outcome}
        />
      )}
      {errors.outcome && <span className="bm-input-error">{errors.outcome}</span>}
    </div>
  );
}

function WinnerWithMethodOutcomeForm({ eventType, winnerChoice, winMethodChoice, setWinnerChoice, setWinMethodChoice, eventOutcomes, errors }) {
  const participants = eventOutcomes?.length > 0
    ? eventOutcomes
    : (eventType ? generateOutcomesFromEventType(eventType) : []);
  // Извлечь уникальных победителей из исходов (P1_..., P2_...)
  const winnerOptions = eventType
    ? Array.from({ length: eventType.participantCountMax || 2 }, (_, i) => `P${i + 1}`)
    : ['P1', 'P2'];
  const winMethods = eventType?.winMethods?.length ? eventType.winMethods : ['KO', 'SUB', 'DECISION'];

  return (
    <div className="bm-input-group">
      <span className="bm-label">
        {outcomeSchemeIcon(OutcomeScheme.WINNER_WITH_METHOD)} Победитель + способ *
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-sm)' }}>
        <div>
          <span className="bm-text-secondary bm-text-sm">Победитель</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {winnerOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`bm-btn bm-btn--sm ${winnerChoice === opt ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
                onClick={() => setWinnerChoice(opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="bm-text-secondary bm-text-sm">Способ победы</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {winMethods.map((method) => (
              <button
                key={method}
                type="button"
                className={`bm-btn bm-btn--sm ${winMethodChoice === method ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
                onClick={() => setWinMethodChoice(method)}
              >
                {method}
              </button>
            ))}
          </div>
        </div>
      </div>
      {winnerChoice && winMethodChoice && (
        <div style={{
          padding: '8px 12px',
          borderRadius: 'var(--bm-radius-lg)',
          background: 'var(--bm-bg-elevated)',
          fontSize: 'var(--bm-text-sm)',
          color: 'var(--bm-accent-primary)',
          fontWeight: 600,
          marginTop: 4,
        }}>
          Твой прогноз: {winnerChoice}_{winMethodChoice}
        </div>
      )}
      {errors.outcome && <span className="bm-input-error">{errors.outcome}</span>}
    </div>
  );
}

function PlacementOutcomeForm({ eventType, placementChoice, setPlacementChoice, eventOutcomes, errors }) {
  const options = eventOutcomes?.length > 0
    ? eventOutcomes
    : (eventType ? generateOutcomesFromEventType(eventType) : ['FIRST', 'SECOND', 'THIRD']);
  const placementLabels = { FIRST: '1-е место', SECOND: '2-е место', THIRD: '3-е место' };

  return (
    <div className="bm-input-group">
      <span className="bm-label">
        {outcomeSchemeIcon(OutcomeScheme.PLACEMENT)} Место *
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`bm-btn bm-btn--sm ${placementChoice === opt ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
            onClick={() => setPlacementChoice(opt)}
          >
            {placementLabels[opt] || opt}
          </button>
        ))}
      </div>
      {errors.outcome && <span className="bm-input-error">{errors.outcome}</span>}
    </div>
  );
}

function IndividualScoreOutcomeForm({ eventType, individualPlacement, individualScore, setIndividualPlacement, setIndividualScore, eventOutcomes, errors }) {
  const placementOptions = ['FIRST', 'SECOND', 'THIRD'];
  const placementLabels = { FIRST: '1-е место', SECOND: '2-е место', THIRD: '3-е место' };
  const scoreMin = eventType?.individualScoreMin ?? 0;
  const scoreMax = eventType?.individualScoreMax ?? 100;
  const precision = eventType?.individualScorePrecision ?? 1;
  const step = Math.pow(10, -precision);

  return (
    <div className="bm-input-group">
      <span className="bm-label">
        {outcomeSchemeIcon(OutcomeScheme.INDIVIDUAL_SCORE)} Место + балл *
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-sm)' }}>
        <div>
          <span className="bm-text-secondary bm-text-sm">Место</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {placementOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`bm-btn bm-btn--sm ${individualPlacement === opt ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
                onClick={() => setIndividualPlacement(opt)}
              >
                {placementLabels[opt]}
              </button>
            ))}
          </div>
        </div>
        <Input
          label="Балл"
          type="number"
          value={individualScore}
          onChange={(e) => setIndividualScore(e.target.value)}
          placeholder={`${scoreMin} — ${scoreMax}`}
          min={scoreMin}
          max={scoreMax}
          step={step}
        />
      </div>
      {individualPlacement && individualScore && (
        <div style={{
          padding: '8px 12px',
          borderRadius: 'var(--bm-radius-lg)',
          background: 'var(--bm-bg-elevated)',
          fontSize: 'var(--bm-text-sm)',
          color: 'var(--bm-accent-primary)',
          fontWeight: 600,
          marginTop: 4,
        }}>
          Твой прогноз: {placementLabels[individualPlacement] || individualPlacement} — {individualScore} баллов
        </div>
      )}
      {errors.outcome && <span className="bm-input-error">{errors.outcome}</span>}
    </div>
  );
}

// ===================================================================================================================
// = OutcomeScheme result form — для фиксации результата по схеме
// ===================================================================================================================

function OutcomeSchemeResultForm({ eventType, outcomeScheme, eventResult, setEventResult, eventOutcomes }) {
  const outcomes = eventOutcomes?.length > 0
    ? eventOutcomes
    : (eventType ? generateOutcomesFromEventType(eventType) : []);

  const placementLabels = { FIRST: '1-е место', SECOND: '2-е место', THIRD: '3-е место' };

  if (outcomeScheme === OutcomeScheme.WINNER_ONLY) {
    return (
      <div className="bm-input-group">
        <span className="bm-label">{outcomeSchemeIcon(outcomeScheme)} Победитель *</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {outcomes.map((chip) => (
            <button
              key={chip}
              type="button"
              className={`bm-btn bm-btn--sm ${eventResult === chip ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
              onClick={() => setEventResult(chip)}
            >
              {chip}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (outcomeScheme === OutcomeScheme.WINNER_WITH_METHOD) {
    return (
      <div className="bm-input-group">
        <span className="bm-label">{outcomeSchemeIcon(outcomeScheme)} Победитель + способ *</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {outcomes.map((chip) => (
            <button
              key={chip}
              type="button"
              className={`bm-btn bm-btn--sm ${eventResult === chip ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
              onClick={() => setEventResult(chip)}
            >
              {chip}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (outcomeScheme === OutcomeScheme.PLACEMENT || outcomeScheme === OutcomeScheme.INDIVIDUAL_SCORE) {
    return (
      <div className="bm-input-group">
        <span className="bm-label">{outcomeSchemeIcon(outcomeScheme)} Место *</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {outcomes.map((chip) => (
            <button
              key={chip}
              type="button"
              className={`bm-btn bm-btn--sm ${eventResult === chip ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
              onClick={() => setEventResult(chip)}
            >
              {placementLabels[chip] || chip}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return null;
}
